import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { resolvePublicFileUrl } from '../../utils/imageUrls';
import { matchesBuildPickerQuery, type BuildPickerOption } from './buildPicker';

export type { BuildPickerOption };

const DEFAULT_INPUT_CLASS = 'input w-full';

interface BuildPickerComboboxProps {
  label: string;
  placeholder?: string;
  helperText?: string;
  selectedHeading?: string;
  options?: BuildPickerOption[];
  /** Remote search. Debounced; results replace the dropdown list. */
  search?: (query: string) => Promise<BuildPickerOption[]>;
  searchMinChars?: number;
  loading?: boolean;
  error?: string | null;
  emptyMessage?: string;
  noMatchMessage?: string;
  selected: BuildPickerOption[];
  onChange: (next: BuildPickerOption[]) => void;
  multiple?: boolean;
  excludeIds?: number[];
  disabled?: boolean;
  inputClassName?: string;
  resultLimit?: number;
  testId?: string;
}

const BuildPickerCombobox: React.FC<BuildPickerComboboxProps> = ({
  label,
  placeholder = 'Search by owner, username, or build…',
  helperText,
  selectedHeading = 'Selected build',
  options: localOptions = [],
  search,
  searchMinChars = 2,
  loading: loadingProp = false,
  error = null,
  emptyMessage = 'No builds to choose from',
  noMatchMessage = 'No matching builds',
  selected,
  onChange,
  multiple = false,
  excludeIds = [],
  disabled = false,
  inputClassName = DEFAULT_INPUT_CLASS,
  resultLimit = 50,
  testId = 'build-picker-search',
}) => {
  const listboxId = useId();
  const inputId = useId();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [remoteOptions, setRemoteOptions] = useState<BuildPickerOption[]>([]);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqIdRef = useRef(0);

  const isRemote = typeof search === 'function';
  const exclude = useMemo(() => new Set(excludeIds.filter((id) => id > 0)), [excludeIds]);
  const selectedIds = useMemo(() => new Set(selected.map((s) => s.id)), [selected]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setHighlight(-1);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const runRemoteSearch = (q: string) => {
    if (!search) return;
    const trimmed = q.trim();
    if (trimmed.length < searchMinChars) {
      setRemoteOptions([]);
      setRemoteLoading(false);
      setSearched(false);
      return;
    }
    const reqId = ++reqIdRef.current;
    setRemoteLoading(true);
    setSearched(true);
    search(trimmed)
      .then((list) => {
        if (reqId !== reqIdRef.current) return;
        setRemoteOptions(Array.isArray(list) ? list : []);
        setRemoteLoading(false);
      })
      .catch(() => {
        if (reqId !== reqIdRef.current) return;
        setRemoteOptions([]);
        setRemoteLoading(false);
      });
  };

  const scheduleRemoteSearch = (q: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runRemoteSearch(q), 250);
  };

  const visibleOptions = useMemo(() => {
    const source = isRemote ? remoteOptions : localOptions;
    const available = source.filter((opt) => !exclude.has(opt.id) && !selectedIds.has(opt.id));
    if (isRemote) return available.slice(0, resultLimit);
    const q = query.trim();
    const filtered = q ? available.filter((opt) => matchesBuildPickerQuery(opt, q)) : available;
    return filtered.slice(0, resultLimit);
  }, [isRemote, remoteOptions, localOptions, exclude, selectedIds, query, resultLimit]);

  useEffect(() => {
    setHighlight(visibleOptions.length > 0 ? 0 : -1);
  }, [visibleOptions]);

  const loading = loadingProp || remoteLoading;

  const handleSelect = (opt: BuildPickerOption) => {
    if (multiple) {
      onChange([...selected, opt]);
      setQuery('');
      setRemoteOptions([]);
      setSearched(false);
      setOpen(true);
      inputRef.current?.focus();
      return;
    }
    onChange([opt]);
    setQuery('');
    setRemoteOptions([]);
    setSearched(false);
    setOpen(false);
    setHighlight(-1);
  };

  const removeSelected = (id: number) => {
    onChange(selected.filter((s) => s.id !== id));
    inputRef.current?.focus();
  };

  const handleClearSingle = () => {
    onChange([]);
    setQuery('');
    inputRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && query === '' && selected.length > 0 && multiple) {
      e.preventDefault();
      removeSelected(selected[selected.length - 1].id);
      return;
    }
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      if (isRemote && query.trim().length >= searchMinChars) scheduleRemoteSearch(query);
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
      setHighlight(-1);
      return;
    }
    if (!open || visibleOptions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % visibleOptions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h <= 0 ? visibleOptions.length - 1 : h - 1));
    } else if (e.key === 'Enter' && highlight >= 0 && visibleOptions[highlight]) {
      e.preventDefault();
      handleSelect(visibleOptions[highlight]);
    }
  };

  const showDropdown =
    open &&
    !error &&
    !loadingProp &&
    (!isRemote || query.trim().length >= searchMinChars || remoteLoading);

  if (!multiple && selected[0]) {
    const current = selected[0];
    const img = resolvePublicFileUrl(current.imageUrl || undefined);
    return (
      <div className="rounded-xl border border-emerald-800/40 bg-emerald-950/20 px-4 py-3 flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-emerald-500/80 mb-1">{selectedHeading}</p>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            {img ? (
              <img src={img} alt="" className="h-12 w-16 object-cover rounded shrink-0" />
            ) : null}
            <div className="min-w-0">
              <p className="font-medium text-white truncate">{current.label}</p>
              {current.subtitle ? <p className="text-sm text-gray-400 truncate">{current.subtitle}</p> : null}
            </div>
          </div>
          <button
            type="button"
            onClick={handleClearSingle}
            disabled={disabled}
            className="text-sm text-gray-400 hover:text-white min-h-[40px] px-2 shrink-0"
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={rootRef} className="relative flex-1 min-w-0">
      <label htmlFor={inputId} className="input-label">
        {label}
      </label>
      {multiple && selected.length > 0 ? (
        <ul className="flex flex-wrap gap-2 mb-2" aria-label="Selected builds">
          {selected.map((opt) => (
            <li key={opt.id}>
              <button
                type="button"
                onClick={() => removeSelected(opt.id)}
                disabled={disabled}
                className="inline-flex items-center gap-2 max-w-full rounded-full border border-white/10 bg-white/[0.06] pl-3 pr-2 py-1.5 text-left text-sm text-white hover:border-white/25 min-h-[36px]"
              >
                <span className="truncate">
                  {opt.label}
                  {opt.subtitle ? (
                    <span className="text-gray-500"> · {opt.subtitle}</span>
                  ) : null}
                </span>
                <span className="text-gray-500 shrink-0" aria-hidden>
                  ×
                </span>
                <span className="sr-only">Remove {opt.label}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <input
        ref={inputRef}
        id={inputId}
        type="search"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={
          highlight >= 0 && visibleOptions[highlight]
            ? `${listboxId}-opt-${visibleOptions[highlight].id}`
            : undefined
        }
        value={query}
        disabled={disabled || loadingProp}
        placeholder={loadingProp ? 'Loading builds…' : placeholder}
        autoComplete="off"
        className={inputClassName}
        data-testid={testId}
        onChange={(e) => {
          const next = e.target.value;
          setQuery(next);
          setOpen(true);
          if (isRemote) scheduleRemoteSearch(next);
        }}
        onFocus={() => {
          setOpen(true);
          if (isRemote && query.trim().length >= searchMinChars) scheduleRemoteSearch(query);
        }}
        onKeyDown={onKeyDown}
      />
      {helperText ? <p className="mt-1 text-xs text-gray-500">{helperText}</p> : null}
      {error ? <p className="mt-1 text-xs text-red-400">{error}</p> : null}
      {isRemote && query.trim().length > 0 && query.trim().length < searchMinChars && open ? (
        <p className="mt-1 text-xs text-gray-500">Type at least {searchMinChars} characters</p>
      ) : null}
      {showDropdown ? (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-xl border border-white/[0.08] bg-[#0a0a0a] shadow-xl"
        >
          {loading && visibleOptions.length === 0 ? (
            <p className="px-4 py-3 text-sm text-gray-500">Searching…</p>
          ) : visibleOptions.length === 0 ? (
            <p className="px-4 py-3 text-sm text-gray-500">
              {isRemote && searched ? noMatchMessage : query.trim() ? noMatchMessage : emptyMessage}
            </p>
          ) : (
            visibleOptions.map((opt, i) => {
              const img = resolvePublicFileUrl(opt.imageUrl || undefined);
              return (
                <button
                  key={opt.id}
                  id={`${listboxId}-opt-${opt.id}`}
                  type="button"
                  role="option"
                  aria-selected={i === highlight}
                  className={`w-full text-left px-4 py-3 min-h-[48px] border-b border-white/[0.06] last:border-0 flex items-center gap-3 ${
                    i === highlight ? 'bg-[#141414]' : 'hover:bg-white/[0.05]'
                  }`}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => handleSelect(opt)}
                >
                  {img ? (
                    <img src={img} alt="" className="h-10 w-14 object-cover rounded shrink-0" />
                  ) : null}
                  <span className="min-w-0">
                    <span className="block font-medium text-white truncate">{opt.label}</span>
                    {opt.subtitle ? (
                      <span className="block text-sm text-gray-400 truncate">{opt.subtitle}</span>
                    ) : null}
                  </span>
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
};

export default BuildPickerCombobox;
